import Container from '@mui/material/Container';
import { styled } from '@mui/material/styles';
import { useErrorContext } from '@qpr/client-library';
import { VerticalScroll } from '@qpr/components';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import createConfigurableGridViewModel, {
  ViewModelType
} from '../../Components/ConfigurableGrid/configurable-grid-view-model';
import ProcessHierarchy from '../components/ProcessHierarchy';
import ProcessSelector from '../components/ProcessSelector';
import ProcessInformationModal from '../components/tabs/ProcessInformationModal';
import initialState, {
  E2Eprocess,
  ProcessIntegrationViewState
} from '../process-integration-state';

const PREFIX = 'ProcessIntegrationView';

const classes = {
  container: `${PREFIX}-container`
};

const StyledContainer = styled(Container)(({ theme }) => ({
  [`&.${classes.container}`]: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    height: 'calc(100vh - 64px)'
  }
}));

interface ProcessIntegrationViewProps {
  viewModel: { getData: () => Promise<ProcessIntegrationViewState> };
  e2eProcessId?: string;
  viewDefinitionId?: string;
}

interface ModalState {
  id?: string;
  viewDefinitionId?: string;
}

const ProcessIntegrationView = ({
  viewModel,
  e2eProcessId,
  viewDefinitionId = ''
}: ProcessIntegrationViewProps) => {
  const [data, setData] = useState(initialState);

  const [modalState, setModalState] = React.useState<ModalState | undefined>(undefined);

  const onModalOpen = (id?: string, modalViewDefinitionId?: string) => {
    setModalState({ id, viewDefinitionId: modalViewDefinitionId });
  };

  const modalViewModel: ViewModelType = createConfigurableGridViewModel(modalState || {});

  const [selectedE2E, setSelectedE2E] = useState<E2Eprocess | null>(null);
  const history = useHistory();

  const { setError } = useErrorContext();

  useEffect(() => {
    const initData = async () => {
      setError(null);
      setSelectedE2E(null);

      const gotData = await viewModel.getData();
      setData(gotData);
      if (gotData.error?.message) {
        setError(gotData.error);
        return;
      }

      if (!e2eProcessId) return;
      const foundE2E = gotData.e2eProcesses.find((e2e: E2Eprocess) => e2e._id === e2eProcessId);
      if (foundE2E) {
        setSelectedE2E(foundE2E);
      } else {
        setError({ message: 'Process not found', details: '' });
      }
    };
    initData();
  }, [viewModel, e2eProcessId]);

  const onSelect = (selected: E2Eprocess | null) => {
    setSelectedE2E(selected);
    // a simple history replace for now, develop more sophisticated mechanism later, when we will
    // have also selected process phases
    history.replace(
      e2eProcessId
        ? `../${selected?._id || '.'}/?viewdefinitionid=${viewDefinitionId}`
        : `./${selected?._id || '.'}/?viewdefinitionid=${viewDefinitionId}`
    );
  };

  return (
    <>
      <ProcessInformationModal
        viewModel={modalViewModel}
        onClose={() => setModalState(undefined)}
        open={!!modalState}
      />
      <VerticalScroll>
        <StyledContainer
          maxWidth={false}
          id={data.e2eProcesses.length > 0 ? 'processIntegrationContainer' : 'loading'}
          data-testid="process-integration-main"
          className={classes.container}
        >
          <ProcessSelector
            processes={data.e2eProcesses}
            onSelect={onSelect}
            selectedValue={selectedE2E}
          />
          <ProcessHierarchy
            onModalOpen={onModalOpen}
            tabs={selectedE2E?.children || []}
            viewDefinitionId={viewDefinitionId}
            firstLevel
          />
        </StyledContainer>
      </VerticalScroll>
    </>
  );
};

export default ProcessIntegrationView;
